import React from "react";
import { Link } from "react-router-dom";
import LandingLayout from "../components/LandingLayout";
import { useRedirectIfLoggedIn } from "../hooks/useRedirectIfLoggedIn";
import LandingHero from "../components/landing/LandingHero";
import ProblemsSection from "../components/landing/ProblemsSection";
import SolutionGrid from "../components/landing/SolutionGrid";
import EarlyAccessCTA from "../components/landing/EarlyAccessCTA";
import FinalCTASection from "../components/landing/FinalCTASection";

const problems = [
    {
        title: "El vendedor no ajusta expectativas",
        description: "Sin un informe objetivo de reforma, no tienes argumentos para convencer al propietario de que su precio no es realista.",
    },
    {
        title: "No puedes responder la pregunta clave",
        description: "\"¿Cuánto costaría reformar esto?\" Siempre acabas derivando a un contacto de confianza, sin método ni cifra. Semanas de espera.",
    },
];

const solutions = [
    {
        icon: "📈",
        title: "Análisis de cualquier propiedad",
        description: "Introduce los datos o pega la URL. En segundos tienes coste de reforma y valor de mercado post-reforma.",
    },
    {
        icon: "📄",
        title: "Informe PDF profesional",
        description: "Desglose por partidas, ROI multiescenario y comparativa. Listo para entregar al cliente en la visita.",
    },
    {
        icon: "💼",
        title: "Panel de gestión",
        description: "Historial de informes por propiedad y cliente. Todo organizado para tu cartera profesional.",
    },
    {
        icon: "🎨",
        title: "Marca blanca (próximamente)",
        description: "Informes con tu logo y tu marca. El cliente ve tu profesionalidad, no la nuestra.",
    },
];

const LandingAgenteInmobiliario = () => {
    useRedirectIfLoggedIn("/analizarpropiedad/nueva");
    return (
        <LandingLayout>
            <div className="max-w-5xl mx-auto px-6 pt-4">
                <Link to="/" className="text-sm text-gray-500 hover:text-brand-blue transition-colors">
                    ← Todos los perfiles
                </Link>
            </div>

            <LandingHero
                badge="Para agentes inmobiliarios"
                title="Cierra operaciones que se te caen"
                highlight="por la reforma"
                subtitle="Ofrece a compradores e inversores el coste de reforma en la misma visita. Convence al vendedor con datos objetivos para ajustar precio. Diferénciate con un servicio que tu competencia no tiene."
                ctaText="Solicitar acceso profesional"
                bgClass="bg-gradient-to-br from-blue-50 to-blue-100"
                highlightClass="text-brand-blue"
            />

            <ProblemsSection
                title="Operaciones enquistadas"
                subtitle="Tienes propiedades fuera de precio de mercado. Sin datos de reforma, estás perdiendo ventas."
                problems={problems}
            />

            <section className="py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-brand-blue mb-4 text-center">Dos herramientas en una</h2>
                    <p className="text-center text-gray-500 text-lg max-w-2xl mx-auto mb-12">
                        Reformista te ayuda a cerrar por los dos lados: con el comprador y con el vendedor.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Para cerrar al comprador / inversor</h3>
                            <ul className="space-y-3">
                                {[
                                    "Coste de reforma estimado en la misma visita",
                                    "ROI calculado en 4 escenarios",
                                    "Informe PDF profesional para compartir",
                                    "Diferénciate de agentes que solo dan el precio",
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                        <span className="text-green-600 font-bold mt-0.5">✓</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Para gestionar al vendedor</h3>
                            <ul className="space-y-3">
                                {[
                                    "Datos objetivos de reforma para ajustar precio",
                                    "\"La vendemos como está, o la reformas y la vendemos por bastante más\"",
                                    "Quita incertidumbre y desbloquea operaciones enquistadas",
                                    "Ganas cartera: vendedores que ajustan precio por datos",
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                        <span className="text-green-600 font-bold mt-0.5">✓</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <SolutionGrid
                title="Qué incluye el acceso profesional"
                solutions={solutions}
            />

            <EarlyAccessCTA />

            <FinalCTASection
                title="Cierra más operaciones con datos que tu competencia no tiene"
                text="Solicita acceso profesional y analiza tu primera propiedad hoy mismo."
                ctaText="Acceder a Reformista"
                rolNombre="gestor_inmobiliario"
            />
        </LandingLayout>
    );
};

export default LandingAgenteInmobiliario;
